// Ruteoptimering: forslag til at flytte eller bytte opgaver mellem steamere på SAMME tid, så den samlede kørsel bliver kortere.
// Kunderne mærker intet (tiden er den samme), så der sendes ingen mail. Samme regler som når der bookes:
// steameren skal dække postnummeret (zone) og ydelsen, være på arbejde, have tid til køretiden mellem opgaverne
// (pause, buffere), og højst have det antal opgaver, der er sat som grænse.
// Reglen "Må flytte kundernes tider" (Opsætning › Booking): optimeringen må også rykke en godkendt booking til en anden
// tid samme dag – højst det sat antal timer fra kundens oprindelige tid. Kunden får ikke besked automatisk; bookingen
// markeres (rute_flyttet), så kontoret kan give kunden besked.
import type { Regler } from './ledige.ts';
import { arbejdstidPaa, koersel, steamerPasser } from './ledige.ts';
import { postnrPunkt } from './geo.ts';
import type { Arbejdstid, Booking, Fravaer, SaerligDag, Steamer, Zone } from './typer.ts';

/** oprindelig: kundens tid (start i minutter). Kun sat, når tiden må flyttes; ellers ligger tiden fast. */
export type RuteJob = { id: number; steamer_id: string; start: number; slut: number; postnr: string; ydelse_id: string; laast: boolean; oprindelig?: number | null };
export type RuteSteamer = { id: string; hjem: string; arbejde: { start: number; slut: number } | null; passer: (postnr: string, ydelse_id: string) => boolean; maxOpgaver: number | null };
/** start/slut: den nye tid, når forslaget flytter kundens tid (ellers samme tid). */
export type RuteFlyt = { id: number; fra: string; til: string; start?: number; slut?: number };
export type RuteForslagData = { type: 'skift' | 'byt' | 'tid'; flyt: RuteFlyt[]; spar: number };
/** "Må flytte kundernes tider": højst maxMin fra kundens tid, starttider hvert trin. minut (eller de faste tider). */
export type TidsFlyt = { maxMin: number; trin: number; faste?: number[] | null };
export type RuteFlytIndst = { aktiv: boolean; maxTimer: number };
export const STANDARD_RUTE_FLYT: RuteFlytIndst = { aktiv: false, maxTimer: 2 };
/** Et forslag, der flytter en kunde, skal spare mindst så mange minutter ekstra (kunden skal have besked). */
const TID_STRAF = 5;

/** Indstillingen → reglerne for at flytte tider (null = må ikke). */
export function tidsFlyt(ind: RuteFlytIndst | undefined, r: Regler): TidsFlyt | null {
  const i = ind ?? STANDARD_RUTE_FLYT;
  return i.aktiv && i.maxTimer > 0 ? { maxMin: Math.round(i.maxTimer * 60), trin: Math.max(5, r.trin || 30), faste: r.faste ?? null } : null;
}

/** Mulige nye starttider for en opgave: inden for vinduet om kundens tid, samme dag, ikke den nuværende tid. */
export function tidsKandidater(j: RuteJob, t: TidsFlyt): number[] {
  if (j.oprindelig == null) return [];
  const varighed = j.slut - j.start;
  const fra = Math.max(0, j.oprindelig - t.maxMin), til = Math.min(1440 - varighed, j.oprindelig + t.maxMin);
  const alle = t.faste?.length ? [...t.faste].sort((a, b) => a - b) : Array.from({ length: Math.floor(1440 / t.trin) }, (_, i) => i * t.trin);
  const ud = alle.filter((x) => x >= fra && x <= til && x !== j.start);
  if (j.oprindelig !== j.start && !ud.includes(j.oprindelig)) ud.push(j.oprindelig);   // tilbage til kundens egen tid
  return ud;
}

/** Overholder en ny tid reglen? Samme dag og højst maxMin fra kundens oprindelige tid. */
export function tidsFlytOk(oprindelig: number, start: number, slut: number, maxMin: number): boolean {
  return start >= 0 && slut <= 1440 && slut > start && Math.abs(start - oprindelig) <= maxMin;
}

const ben = (a: string, b: string) => koersel(postnrPunkt(a), postnrPunkt(b));

/** Samlet kørsel for en steamers dag: hjemmefra → første → … → sidste. */
export function rutensKoersel(hjem: string, jobs: RuteJob[]): number {
  const l = [...jobs].sort((a, b) => a.start - b.start);
  let sum = 0, fra = hjem;
  for (const j of l) { sum += ben(fra, j.postnr); fra = j.postnr; }
  return sum;
}

/** Kan steameren nå alle opgaverne efter reglerne? */
export function ruteMulig(s: RuteSteamer, jobs: RuteJob[], r: Regler): boolean {
  if (!s.arbejde) return jobs.length === 0;
  const max = s.maxOpgaver ?? r.maxOpgaver;
  if (max != null && max > 0 && jobs.length > max) return false;
  const bF = r.bufferFoer ?? 0, bE = r.bufferEfter ?? 0;
  const l = [...jobs].sort((a, b) => a.start - b.start);
  for (let i = 0; i < l.length; i++) {
    const j = l[i];
    if (!s.passer(j.postnr, j.ydelse_id)) return false;
    if (j.start < s.arbejde.start + bF || j.slut + bE > s.arbejde.slut) return false;
    const k = i ? ben(l[i - 1].postnr, j.postnr) : 0;
    if (r.maxKoersel && k > r.maxKoersel) return false;
    if (i && j.start < l[i - 1].slut + bE + Math.max(r.pause, k) + bF) return false;
  }
  return true;
}

export function ruteForslag(jobsIn: RuteJob[], steamere: RuteSteamer[], r: Regler, o: { minSpar?: number; maks?: number; tid?: TidsFlyt | null } = {}): RuteForslagData[] {
  const minSpar = o.minSpar ?? 8, maks = o.maks ?? 12;
  const jobs = jobsIn.map((j) => ({ ...j }));
  const S = new Map(steamere.map((s) => [s.id, s]));
  const af = (sid: string) => jobs.filter((j) => j.steamer_id === sid);
  const cost = (sid: string, l = af(sid)) => rutensKoersel(S.get(sid)!.hjem, l);
  const ud: RuteForslagData[] = [];
  const tidFlyttet = new Set<number>();   // en kunde flyttes højst én gang pr. runde forslag
  for (let runde = 0; runde < maks; runde++) {
    let bedst: { score: number; anvend: () => void; f: RuteForslagData } | null = null;
    const flytbare = jobs.filter((j) => !j.laast && S.has(j.steamer_id) && !tidFlyttet.has(j.id));
    for (const j of flytbare) {
      const A = j.steamer_id, aUden = af(A).filter((x) => x.id !== j.id), cA = cost(A);
      for (const B of steamere) {
        if (B.id === A) continue;
        // 1) Flyt j til B
        const bMed = [...af(B.id), { ...j, steamer_id: B.id }];
        if (ruteMulig(B, bMed, r)) {
          const spar = cA + cost(B.id) - cost(A, aUden) - cost(B.id, bMed);
          if (spar >= minSpar && (!bedst || spar > bedst.score)) bedst = { score: spar, f: { type: 'skift', flyt: [{ id: j.id, fra: A, til: B.id }], spar }, anvend: () => { j.steamer_id = B.id; } };
        }
        // 2) Byt j med en af B's opgaver
        for (const k of af(B.id)) {
          if (k.laast || k.id < j.id || tidFlyttet.has(k.id)) continue;
          const nA = [...aUden, { ...k, steamer_id: A }], nB = [...af(B.id).filter((x) => x.id !== k.id), { ...j, steamer_id: B.id }];
          if (!ruteMulig(S.get(A)!, nA, r) || !ruteMulig(B, nB, r)) continue;
          const spar = cA + cost(B.id) - cost(A, nA) - cost(B.id, nB);
          if (spar >= minSpar && (!bedst || spar > bedst.score)) bedst = { score: spar, f: { type: 'byt', flyt: [{ id: j.id, fra: A, til: B.id }, { id: k.id, fra: B.id, til: A }], spar }, anvend: () => { j.steamer_id = B.id; k.steamer_id = A; } };
        }
      }
      // 3) Ny tid (samme eller en anden steamer) – kun når reglen er slået til, og kun inden for vinduet
      if (!o.tid || j.oprindelig == null) continue;
      const aOk = ruteMulig(S.get(A)!, aUden, r);
      for (const start of tidsKandidater(j, o.tid)) {
        const slut = start + (j.slut - j.start);
        if (!tidsFlytOk(j.oprindelig, start, slut, o.tid.maxMin)) continue;
        for (const B of steamere) {
          const nj = { ...j, steamer_id: B.id, start, slut };
          const ny = B.id === A ? [...aUden, nj] : [...af(B.id), nj];
          if ((B.id !== A && !aOk) || !ruteMulig(B, ny, r)) continue;
          const spar = B.id === A ? cA - cost(A, ny) : cA + cost(B.id) - cost(A, aUden) - cost(B.id, ny);
          const score = spar - TID_STRAF;
          if (score >= minSpar && (!bedst || score > bedst.score))
            bedst = { score, f: { type: 'tid', flyt: [{ id: j.id, fra: A, til: B.id, start, slut }], spar }, anvend: () => { Object.assign(j, { steamer_id: B.id, start, slut }); tidFlyttet.add(j.id); } };
        }
      }
    }
    if (!bedst) break;
    bedst.anvend(); ud.push(bedst.f);
  }
  return ud;
}

/** Samlet kørsel for alle steamere – før og efter forslagene. */
export function samletKoersel(jobs: RuteJob[], steamere: RuteSteamer[], flyt: RuteFlyt[] = []): number {
  const l = jobs.map((j) => { const m = flyt.filter((x) => x.id === j.id).pop(); return m ? { ...j, steamer_id: m.til, start: m.start ?? j.start, slut: m.slut ?? j.slut } : j; });
  return steamere.reduce((s, st) => s + rutensKoersel(st.hjem, l.filter((j) => j.steamer_id === st.id)), 0);
}

/** Dagens opgaver og steamere til ruteoptimeringen (samme i testversionen og i drift). */
export function ruteDag(a: {
  dato: string; idag: string; nu: number; flytTider: boolean;
  bookinger: Pick<Booking, 'id' | 'dato' | 'steamer_id' | 'start' | 'slut' | 'status' | 'postnr' | 'ydelse_id' | 'rute_flyttet'>[];
  steamere: Steamer[]; arbejdstider: Arbejdstid[]; fravaer: Fravaer[]; saerligeDage: SaerligDag[]; lukkedage: string[]; zoner: Zone[];
}): { jobs: RuteJob[]; steamere: RuteSteamer[] } {
  const jobs: RuteJob[] = a.bookinger.filter((b) => b.dato === a.dato && b.steamer_id && b.start != null && b.slut != null && ['afventer', 'godkendt', 'i_gang', 'faerdig'].includes(b.status)).map((b) => {
    const laast = !['godkendt', 'afventer'].includes(b.status) || (a.dato === a.idag && b.start! < a.nu + 60) || a.dato < a.idag;
    // Kun godkendte bookinger kan få ny tid (ventende har kundens ønskede tid, til de er godkendt)
    const oprindelig = a.flytTider && !laast && b.status === 'godkendt' ? b.rute_flyttet?.fra ?? b.start! : null;
    return { id: b.id, steamer_id: b.steamer_id!, start: b.start!, slut: b.slut!, postnr: b.postnr, ydelse_id: b.ydelse_id, laast, oprindelig };
  });
  const steamere: RuteSteamer[] = a.steamere.filter((s) => s.aktiv).map((s) => ({
    id: s.id, hjem: s.start_postnr, maxOpgaver: s.maxOpgaver ?? null,
    arbejde: arbejdstidPaa({ steamer: s, dato: a.dato, arbejdstider: a.arbejdstider, fravaer: a.fravaer, saerligeDage: a.saerligeDage, lukkedage: a.lukkedage }),
    passer: (postnr: string, ydelse_id: string) => steamerPasser(s, postnr, a.zoner, ydelse_id),
  }));
  return { jobs, steamere };
}

/**
 * Tjek et forslag mod dagens plan lige før det anvendes. Svarer med en fejltekst, eller null hvis det kan gennemføres.
 * jobs ændres til den nye plan.
 */
export function tjekRuteFlyt(jobs: RuteJob[], steamere: RuteSteamer[], r: Regler, flyt: RuteFlyt[], tid: TidsFlyt | null): string | null {
  for (const m of flyt) {
    const j = jobs.find((x) => x.id === m.id);
    if (!j || j.laast) return 'En af opgaverne kan ikke flyttes længere. Opdater forslagene.';
    if (j.steamer_id !== m.fra) return 'Planen er ændret siden forslaget. Opdater forslagene.';
    if (m.start != null && m.start !== j.start) {
      if (!tid) return 'Ruteoptimeringen må ikke flytte kundernes tider. Slå det til under Opsætning › Booking.';
      if (j.oprindelig == null) return 'Kun godkendte bookinger kan få en ny tid.';
      const slut = m.start + (j.slut - j.start);
      if (!tidsFlytOk(j.oprindelig, m.start, slut, tid.maxMin)) return `En kunde må højst flyttes ${String(tid.maxMin / 60).replace('.', ',')} timer fra sin tid og ikke til en anden dag.`;
      Object.assign(j, { start: m.start, slut });
    }
    j.steamer_id = m.til;
  }
  for (const sid of new Set(flyt.flatMap((m) => [m.fra, m.til]))) {
    const s = steamere.find((x) => x.id === sid);
    if (!s || !ruteMulig(s, jobs.filter((j) => j.steamer_id === sid), r)) return 'Forslaget passer ikke længere med reglerne. Opdater forslagene.';
  }
  return null;
}

/** Et forslag, som kontoret ser det: hvem kører, og for "tid" kundens nye tid. */
export type RuteForslagVisning = {
  noegle: string; type: RuteForslagData['type']; spar: number;
  flyt: { id: number; fra: string; til: string; fraNavn: string; tilNavn: string; start: number; slut: number; nyStart?: number; nySlut?: number; kunde: string; by: string }[];
};
/** Bookinger, ruteoptimeringen har givet en ny tid, hvor kunden endnu ikke har fået besked. */
export type RuteBesked = { id: number; kunde: string; telefon: string; fra: number; start: number; slut: number };

/** Forslagene for en dag med samlet kørsel før/efter og navne på steamere og kunder. */
export function ruteResultat(jobs: RuteJob[], steamere: RuteSteamer[], r: Regler, tid: TidsFlyt | null,
  navn: (steamer_id: string) => string, kunde: (booking_id: number) => { kunde: string; by: string }): { foer: number; efter: number; forslag: RuteForslagVisning[] } {
  const forslag = ruteForslag(jobs, steamere, r, { tid });
  return {
    foer: samletKoersel(jobs, steamere), efter: samletKoersel(jobs, steamere, forslag.flatMap((f) => f.flyt)),
    forslag: forslag.map((f) => ({ ...f, noegle: f.flyt.map((m) => `${m.id}:${m.til}${m.start != null ? '@' + m.start : ''}`).join('|'), flyt: f.flyt.map((m) => {
      const j = jobs.find((x) => x.id === m.id)!;
      return { id: m.id, fra: m.fra, til: m.til, fraNavn: navn(m.fra), tilNavn: navn(m.til), start: j.start, slut: j.slut, ...(m.start != null ? { nyStart: m.start, nySlut: m.slut } : {}), ...kunde(m.id) };
    }) })),
  };
}
